// 15.E Часть 1 — тарифы внутри продукта (гейтинг UI).
//
// Тариф проекта: null (не активирован) | 'express' | 'full'.
//   express (Reading B): по 1 прогону КАЖДОЙ проверки независимо
//     (комплектность / нормы / соответствие заданию), но без ПЕРЕзапуска;
//     без исправленной редакции; без сравнения.
//   full: без ограничений.
//   null: можно грузить исходные документы, но нельзя запускать проверки.
//
// Активация — вручную админом через API (POST /projects/{id}/tariff);
// отдельного UI управления тарифом пока нет (см. follow-up).
import { ApiError } from '@/api/client';
import type { Tariff, CheckKind, RunsByCheck } from '@/types/api';

export interface TariffState {
  tariff: Tariff;
  runsByCheck: RunsByCheck;
  snapshotsCount: number;
}

/** Сообщение upsell при исчерпании прогона конкретной проверки на «Экспресс». */
export const EXPRESS_RUN_USED_MSG =
  'На тарифе «Разовый прогон» эта проверка уже выполнена. Перейдите на ' +
  '«Доведение до экспертизы» для повторных проверок.';

/** Можно ли запустить КОНКРЕТНУЮ проверку (Reading B — по каждой отдельно). */
export function canRunCheck(s: TariffState | undefined, check: CheckKind): boolean {
  if (!s) return true; // данных ещё нет — не блокируем оптимистично
  if (s.tariff === 'full') return true;
  if (s.tariff === 'express') return (s.runsByCheck[check] ?? 0) < 1;
  return false; // null — не активирован
}

/** Можно ли грузить исправленную редакцию / пользоваться сравнением. */
export function canRedaction(s: TariffState | undefined): boolean {
  if (!s) return true;
  return s.tariff === 'full';
}

/** Причина блокировки запуска КОНКРЕТНОЙ проверки (или null, если можно). */
export function runBlockReason(s: TariffState | undefined, check: CheckKind): string | null {
  if (!s) return null;
  if (s.tariff === 'full') return null;
  if (s.tariff === 'express') {
    if ((s.runsByCheck[check] ?? 0) >= 1) return EXPRESS_RUN_USED_MSG;
    return null;
  }
  // null
  return 'Активируйте тариф, чтобы запустить проверку.';
}

/** Собрать TariffState из полей проекта (централизует дефолты). */
export function tariffStateFromProject(
  p: { tariff?: Tariff; runs_by_check?: RunsByCheck; snapshots_count?: number } | undefined,
): TariffState | undefined {
  if (!p) return undefined;
  return {
    tariff: p.tariff ?? null,
    runsByCheck: p.runs_by_check ?? {},
    snapshotsCount: p.snapshots_count ?? 0,
  };
}

/** Человекочитаемая метка тарифа для бейджа. */
export function tariffLabel(t: Tariff): string {
  if (t === 'express') return 'Тариф: Разовый прогон';
  if (t === 'full') return 'Тариф: Доведение до экспертизы';
  return 'Тариф не активирован';
}

/** Цвет бейджа тарифа (фирменные: тёмно-синий / амбер / серый). */
export function tariffBadgeColors(t: Tariff): { bg: string; fg: string; border: string } {
  if (t === 'full') return { bg: '#ECFDF5', fg: '#047857', border: '#A7F3D0' };
  if (t === 'express') return { bg: '#FFFBEB', fg: '#B45309', border: '#FDE68A' };
  return { bg: '#F3F4F6', fg: '#6B7280', border: '#E5E7EB' };
}

/** Достать сообщение тарифного гейта из ошибки API (402), иначе null. */
export function tariffErrorMessage(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null;
  if (e.status !== 402) return null;
  const d = e.detail as { detail?: { error?: string; message?: string } } | undefined;
  const inner = d?.detail;
  if (inner && typeof inner.message === 'string') return inner.message;
  return 'Действие недоступно на текущем тарифе.';
}
